import { ActionReducerMap, createSelector, createFeatureSelector } from '@ngrx/store';
import { FormlyFieldConfig } from '@ngx-formly/core';
import * as formSchemaState from './form-schema/form-schema.reducer';
import * as formData from './form-data/form-data.reducer';
import * as intakeSection from './form-schema/intake-section.reducers';
import * as apsAssigned from './aps_assinged/aps_assinged.reducer';
import { ApsFormlySchema } from '../getcare-formly/aps-formly';
import { ApsAssignedList } from './../models/aps-assigned-list';

export interface AppState {
  form_schema: ApsFormlySchema;
  form_data: any;
  dropdown_options: any;
  intake_sections: any;
  aps_assinged: Array<ApsAssignedList>;
}

export const reducers: ActionReducerMap<AppState> = {
  form_schema: formSchemaState.FormSchemaReducers,
  form_data: formData.FormDataReducers,
  dropdown_options: formSchemaState.DropdownOptionsReducers,
  intake_sections: intakeSection.IntakeSectionReducers,
  aps_assinged: apsAssigned.ApsAssignedReducers
};

export const selectFormSchemaState = (state: AppState) => state.form_schema;
export const selectFormDataState = (state: AppState) => state.form_data;
export const selectDropdownOptionState = (state: AppState) => state.dropdown_options;
export const selectIntakeSectionsState = (state: AppState) => state.intake_sections;
export const selectApsAssingedState = (state: AppState) => state.aps_assinged;

export const formSchemaSelector = createSelector(
  selectFormSchemaState,
  (state: AppState['form_schema'], prop?: any) => state
);

export const formDataSelector = createSelector(
  selectFormDataState,
  (state: AppState['form_data']) => state
);

export const selectSpecificformSchemaSelector = createSelector(
  selectFormSchemaState,
  selectIntakeSectionsState,
  (state: ApsFormlySchema, sections: AppState['intake_sections'], prop?: any) => {
    let finalSchema = state[prop.parentSection];

    if (finalSchema === undefined
      || (prop.parentSection === 'client_identification_demographics' && prop.subSection === 'additional_info')) {
      return state;
    }

    if (prop.subSection !== null && prop.subSection !== 'null') {
      sections.filter(route => route.key === prop.parentSection).map(
        sub => {
          sub.subSections.filter(subRoutes => subRoutes.id === prop.subSection).map(
            data => {
              if (data.sub_section !== undefined) {
                finalSchema = [
                  {
                    ...finalSchema[0],
                    key: finalSchema[0].key,
                    fieldGroup: [
                      ...finalSchema[0].fieldGroup.filter( (schema) => data.sections.includes(schema.id) )
                      .map(subSection => {
                        return {
                          ...subSection,
                          fieldGroup: [
                            ...subSection.fieldGroup.map(
                              subData => {
                                return {
                                  ...subData,
                                  fieldArray: {
                                    fieldGroup: [
                                      ...subData.fieldArray.fieldGroup.filter( (subSchema) => data.sub_section.includes(subSchema.id) )
                                    ]
                                  }
                                };
                              }
                            )
                          ]
                        };
                      })
                    ]
                  }
                ];
              } else {
                finalSchema = [
                  {
                    ...finalSchema[0],
                    key: finalSchema[0].key,
                    fieldGroup: [
                      ...finalSchema[0].fieldGroup.filter( (schema) => data.sections.includes(schema.id) )
                    ]
                  }
                ];
              }
            }
          );
        }
      );
    }
    return {
      mainSection: finalSchema,
      subSection: state[prop.parentSection][0].fieldGroup
      .filter( schema => schema.key !== undefined ).map(schema => schema.key)
    };
  }
);

export const dropdownOptionsSelector = createSelector(
  selectDropdownOptionState,
  (state: AppState['dropdown_options']) => state
);

export const intakeSectionSelector = createSelector(
  selectIntakeSectionsState,
  (state: AppState['intake_sections']) => state
);

export const formDataSpecifySelector = createSelector(
  selectFormDataState,
  selectIntakeSectionsState,
  (state: AppState['form_data'], sections: AppState['intake_sections'], prop?: any) => {
    if (state !== undefined) {
      let jsonKey = '';
      sections.filter(data => data.key === prop.section).map(
        section => jsonKey = section.json_key
      );
      if (prop.section === 'client_identification_demographics') {
        jsonKey = 'investigation';
      }
      if (prop.section === 'investigation_full') {
        return state.investigation;
      }
      if (prop.section === 'participant_fcsprelationship_info') {
        return state.participant_fcsprelationship_info;
      }
      if (state[jsonKey] !== undefined) {
        if (jsonKey === 'intake_report' && prop.subSection === 'sectionI') {
          const data = {...JSON.parse(state[jsonKey].json_data)};
          let receivingAgencyAgent = '';
          let approvedbyAgent = '';
          let assignedAgent = '';
          if (state.receiving_agency_info.report_received_by_agent !== undefined) {
            // tslint:disable-next-line: max-line-length
            receivingAgencyAgent = state.receiving_agency_info.report_received_by_agent.last_name + ', ' + state.receiving_agency_info.report_received_by_agent.first_name;
          }
          if (state.receiving_agency_info.approvedby_agent !== undefined) {
            // tslint:disable-next-line: max-line-length
            approvedbyAgent = state.receiving_agency_info.approvedby_agent.last_name + ', ' + state.receiving_agency_info.approvedby_agent.first_name;
          }
          if (state.receiving_agency_info.assigned_agent !== undefined) {
            // tslint:disable-next-line: max-line-length
            assignedAgent = state.receiving_agency_info.assigned_agent.last_name + ', ' + state.receiving_agency_info.assigned_agent.first_name;
          }
          return {
              aps_report_load: {
                ...data.aps_report_load,
                receiving_agency: {
                  ...data.aps_report_load.receiving_agency,
                  report_received_by: receivingAgencyAgent,
                  report_received_date: state.receiving_agency_info.report_received_date,
                  report_received_time: state.receiving_agency_info.report_received_time,
                  approved_by: approvedbyAgent,
                  assigned_to: assignedAgent,
                  aps_blocked_user_accesses: [
                    ...state.receiving_agency_info.aps_blocked_user_accesses
                  ]
                }
              }
          };
        }
        if (jsonKey === 'intake_report' || jsonKey === 'investigation') {
          return JSON.parse(state[jsonKey].json_data);
        }
        return state[jsonKey];
      }
    }
    return state;
  }
);

export const apsAssignedSelector = createSelector(
  selectApsAssingedState,
  (state: Array<ApsAssignedList>) => state
);
